import { Dictionary } from 'Dictionary';
import { useDictionary } from './useDictionary';
import { DICTIONARY } from 'Enum';
import { useNavigate } from 'react-router-dom';
import { getLastOrderId, getMedConfirmationData } from 'API';
import {
  getForm106,
  getForm106File,
  getPaycheck,
  getPayslipFile,
} from 'API/Paychecks/paychecks';
import { downloadFile } from 'Utils';
import {
  IDisabilityConfirmation,
  getDisabilityConfirmationPdf,
  getDisabilityConfirmations,
} from 'API/disabilityConfirmation';
import { useEffect, useState } from 'react';
import { useImposter } from './useImposter';
import { sendEvent } from 'Utils/sendEvent';
import { getSupplierRedirectCode } from 'API/Treatment/treatment';
import { eshetTursRedirectCode, flyingCarpetRedirectCode } from 'Config';

export interface IActivity {
  id: number;
  title: string;
  activities: {
    id: number;
    label: string;
    onClick?: () => void;
    showIcon?: boolean;
    searchWords: string[];
  }[];
}
export const useActivitiesLobby = () => {
  const [disabilityConfirmations, setDisabilityConfirmations] = useState<
    IDisabilityConfirmation[]
  >([]);

  const { imposterId } = useImposter();

  const dictionary = useDictionary(Dictionary, DICTIONARY.ActivitiesLobby);
  const words = useDictionary(Dictionary, DICTIONARY.ActivitiesSearchWords);
  const navigate = useNavigate();

  const init = async () => {
    const disabilityConfirmations = await getDisabilityConfirmations();
    if (disabilityConfirmations)
      setDisabilityConfirmations(disabilityConfirmations);
  };

  useEffect(() => {
    init();
  }, [imposterId]);

  const confirmationsSearchWords: { [key: number]: string[] } = {
    1: [
      words.approval,
      words.approvals,
      words.forDownload,
      words.downloadOf,
      words.discount,
      words.propertyTex,
    ],
    2: [
      words.approval,
      words.approvals,
      words.forDownload,
      words.downloadOf,
      words.exemption,
      words.payment,
      words.toll,
      words.fee,
      words.registration,
      words.enrollment,
      words.mortgage,
    ],
    3: [
      words.approval,
      words.approvals,
      words.forDownload,
      words.downloadOf,
      words.discount,
      words.payment,
      words.tex,
      words.texes,
      words.purchase,
      words.procurement,
    ],
    4: [
      words.approval,
      words.approvals,
      words.disability,
      words.disabled,
      words.general,
      words.forDownload,
      words.downloadOf,
      words.abroadShortened,
      words.abroad,
      words.drive,
      words.flight,
    ],
    5: [
      words.approval,
      words.approvals,
      words.disability,
      words.disabled,
      words.general,
      words.forDownload,
      words.downloadOf,
    ],
    6: [
      words.approval,
      words.approvals,
      words.disability,
      words.disabled,
      words.general,
      words.forDownload,
      words.downloadOf,
      words.detailed,
      words.including,
      words.section,
      words.sections,
      words.sectionsOf,
      words.percent,
      words.percentage,
      words.percents,
    ],
    7: [
      words.approval,
      words.approvals,
      words.forDownload,
      words.downloadOf,
      words.discount,
      words.payment,
      words.payments,
      words.fees,
      words.agreement,
      words.transfer,
      words.realEstate,
    ],
    8: [
      words.approval,
      words.approvals,
      words.disability,
      words.disabled,
      words.general,
      words.forDownload,
      words.downloadOf,
    ],
  };

  const activitiesList: IActivity[] = [
    {
      id: 1,
      title: dictionary.payments.title,
      activities: [
        {
          id: 1,
          label: dictionary.payments.lastPaycheck,
          onClick: async () => {
            sendEvent(
              'Activities Page',
              'ActionDownload',
              dictionary.payments.lastPaycheck
            );
            const lastPaycheck = await getPaycheck();
            if (lastPaycheck) {
              const file = await getPayslipFile(lastPaycheck.fileId);
              if (file)
                downloadFile(
                  file.pdfSource,
                  '.pdf',
                  dictionary.lastPaycheck,
                  false
                );
            } else throw new Error(dictionary.missingPaycheckError);
          },
          showIcon: true,
          searchWords: [
            words.paycheck,
            words.paychecks,
            words.wage,
            words.reward,
            words.rewards,
            words.money,
            words.salary,
            words.latest,
            words.previous,
            words.lastOnes,
            words.predecessors,
          ],
        },
        {
          id: 2,
          label: dictionary.payments.allPaychecks,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.payments.allPaychecks
            );
            navigate('/paychecks');
          },
          searchWords: [
            words.paycheck,
            words.paychecks,
            words.wage,
            words.reward,
            words.rewards,
            words.money,
            words.salary,
            words.latest,
            words.previous,
            words.lastOnes,
            words.predecessors,
          ],
        },
        {
          id: 3,
          label: dictionary.payments.paymentsAndDeductions,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.payments.paymentsAndDeductions
            );
            navigate('/payments&deductions');
          },
          searchWords: [
            words.payment,
            words.payments,
            words.deduction,
            words.deductions,
            words.wage,
            words.reward,
            words.rewards,
            words.reimbursements,
            words.return,
            words.bonus,
            words.bonuses,
            words.detail,
            words.invoice,
            words.invoices,
          ],
        },
        {
          id: 4,
          label: dictionary.payments.last106,
          onClick: async () => {
            sendEvent(
              'Activities Page',
              'ActionDownload',
              dictionary.payments.last106
            );
            const lastForm106 = await getForm106();
            if (lastForm106) {
              const file = await getForm106File(lastForm106.id);
              if (file)
                downloadFile(
                  file.pdfSource,
                  '.pdf',
                  dictionary.lastForm106,
                  false
                );
            } else throw new Error(dictionary.missingForm106Error);
          },
          showIcon: true,
          searchWords: [
            words.form,
            words.forms,
            words.form106,
            words.file,
            words.files,
            words.latest,
            words.lastOnes,
          ],
        },
        {
          id: 5,
          label: dictionary.payments.all106,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.payments.all106
            );
            navigate('/paychecks/1');
          },
          searchWords: [
            words.form,
            words.forms,
            words.form106,
            words.file,
            words.files,
            words.latest,
            words.lastOnes,
          ],
        },
      ],
    },
    {
      id: 2,
      title: dictionary.referrals.title,
      activities: [
        {
          id: 1,
          label: dictionary.referrals.newReferral,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.referrals.newReferral
            );
            navigate('/universalRequest/1/1/77');
          },
          searchWords: [
            words.inquiry,
            words.inquiries,
            words.new,
            words.request,
            words.requests,
            words.wing,
            words.referral,
            words.referrals,
            words.medical,
            words.medicals,
          ],
        },
        {
          id: 2,
          label: dictionary.referrals.serviceProviders,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.referrals.serviceProviders
            );
            window.open(
              'https://forms.mod.gov.il/RehabSuppliers/index.html',
              '_blank'
            );
          },
          searchWords: [
            words.supplier,
            words.suppliers,
            words.service,
            words.expert,
            words.experts,
            words.equipment,
            words.medical,
            words.location,
            words.search,
          ],
        },
        {
          id: 3,
          label: dictionary.referrals.allReferrals,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.referrals.allReferrals
            );
            navigate('/medical-referrals');
          },
          searchWords: [
            words.referral,
            words.referrals,
            words.treatment,
            words.medical,
            words.medicals,
            words.notExpired,
            words.expired,
          ],
        },
      ],
    },
    {
      id: 3,
      title: dictionary.contact.title,
      activities: [
        {
          id: 1,
          label: dictionary.contact.contactWing,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.contact.contactWing
            );
            navigate('/universalRequest');
          },
          searchWords: [
            words.inquiry,
            words.inquiries,
            words.new,
            words.request,
            words.requests,
            words.wing,
            words.district,
            words.submit,
            words.submission,
          ],
        },
        {
          id: 2,
          label: dictionary.contact.contactTeam,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.contact.contactTeam
            );
            navigate('/personal-team');
          },
          searchWords: [
            words.creation,
            words.create,
            words.connection,
            words.connect,
            words.communication,
            words.team,
            words.personal,
            words.your,
            words.phone,
            words.phones,
            words.address,
            words.district,
            words.email,
            words.mail,
            words.mails,
          ],
        },
        {
          id: 3,
          label: dictionary.contact.bookMeeting,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.contact.bookMeeting
            );
            window.open('https://myvisit.com/#!/home/signin/', '_blank');
          },
          searchWords: [
            words.summon,
            words.invite,
            words.establish,
            words.turn,
            words.turns,
            words.district,
            words.districts,
            words.team,
            words.meeting,
          ],
        },
        {
          id: 4,
          label: dictionary.contact.summary,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.contact.summary
            );
            navigate('/applies');
          },
          searchWords: [
            words.inquiry,
            words.inquiries,
            words.wing,
            words.request,
            words.requests,
            words.treatmentStatus,
            words.status,
            words.statuses,
            words.track,
            words.all,
          ],
        },
      ],
    },
    {
      id: 4,
      title: dictionary.treatments.title,
      activities: [
        {
          id: 1,
          label: dictionary.treatments.toTreatments,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/treatment');
          },
          searchWords: [
            words.home,
            words.homes,
            words.hotel,
            words.recreation,
            words.recreations,
            words.recovery,
            words.vacation,
            words.invitation,
            words.invitations,
            words.treatment,
            words.medical,
            words.heat,
            words.cure,
            words.healing,
          ],
        },
        {
          id: 2,
          label: dictionary.treatments.flyingCarpet,
          onClick: async () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.flyingCarpet
            );
            const code = await getSupplierRedirectCode(
              flyingCarpetRedirectCode
            );
            if (code)
              window.open(
                `${
                  Dictionary[DICTIONARY.General].flyingCarpetUrl
                }/?code=${code}`,
                '_blank'
              );
          },
          searchWords: [
            words.home,
            words.homes,
            words.hotel,
            words.recreation,
            words.recreations,
            words.recovery,
            words.vacation,
            words.invitation,
            words.invitations,
            words.treatment,
            words.medical,
            words.heat,
            words.cure,
            words.healing,
            words.site,
            words.flyingCarpet,
          ],
        },
        {
          id: 3,
          label: dictionary.treatments.eshetTours,
          onClick: async () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            const code = await getSupplierRedirectCode(eshetTursRedirectCode);
            if (code)
              window.open(
                `${Dictionary[DICTIONARY.General].eshetTursUrl}/?code=${code}`,
                '_blank'
              );
          },
          searchWords: [
            words.home,
            words.homes,
            words.hotel,
            words.recreation,
            words.recreations,
            words.recovery,
            words.vacation,
            words.invitation,
            words.invitations,
            words.treatment,
            words.medical,
            words.heat,
            words.cure,
            words.healing,
            words.site,
            words.eshetTours,
          ],
        },
        {
          id: 4,
          label: dictionary.treatments.accessibleHotels,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            window.open(
              'https://shikum.mod.gov.il/medical/relaxation/hotel-accesability',
              '_blank'
            );
          },
          searchWords: [
            words.home,
            words.homes,
            words.hotel,
            words.recreation,
            words.recreations,
            words.recovery,
            words.vacation,
            words.invitation,
            words.invitations,
            words.treatment,
            words.medical,
            words.heat,
            words.cure,
            words.healing,
            words.approachable,
            words.accessible,
            words.accessible2,
            words.accessibility,
          ],
        },
      ],
    },
    {
      id: 5,
      title: dictionary.compassRights.title,
      activities: [
        {
          id: 1,
          label: dictionary.compassRights.toCompassRights,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/compass-rights');
          },
          searchWords: [
            words.compass,
            words.right,
            words.entitlement,
            words.rights,
            words.entitlements,
            words.percent,
            words.percentage,
            words.disability,
          ],
        },
        {
          id: 2,
          label: dictionary.compassRights.longTermSupplies,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.compassRights.longTermSupplies
            );
            navigate('/longterm-equipment');
          },
          searchWords: [
            words.entitlement,
            words.entitlements,
            words.card,
            words.cards,
            words.equipment,
            words.long,
            words.range,
            words.right,
            words.rights,
            words.percent,
            words.percentage,
            words.disability,
            words.info,
          ],
        },
        {
          id: 3,
          label: dictionary.compassRights.toInfo,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            window.open('https://shikum.mod.gov.il/', '_blank');
          },
          searchWords: [
            words.compass,
            words.right,
            words.entitlement,
            words.rights,
            words.entitlements,
            words.percent,
            words.percentage,
            words.disability,
            words.info,
          ],
        },
        // {
        //   id: 4,
        //   label: dictionary.compassRights.rightsCards,
        //   onClick: () => {
        //     // TODO enable rights cards when ready
        //   },
        //   searchWords: [],
        // },
      ],
    },
    {
      id: 6,
      title: dictionary.medicine.title,
      activities: [
        {
          id: 1,
          label: dictionary.medicine.order,
          onClick: () => {
            // TODO enable medicine to open on request new medicine
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/medicine');
          },
          searchWords: [
            words.medication,
            words.medications,
            words.permanent,
            words.invitation,
            words.invitations,
            words.new,
            words.news,
          ],
        },
        // {
        //   id: 2,
        //   label: dictionary.medicine.duplicate,
        //   onClick: () => {
        //     // TODO enable duplicate last order, add get last shemesh order to api
        //     sendEvent('ActionClick', dictionary.treatments.eshetTours);
        //     navigate('/medicine');
        //   },
        //   searchWords: [
        //     words.medication,
        //     words.medications,
        //     words.permanent,
        //     words.invitation,
        //     words.invitations,
        //     words.new,
        //     words.news,
        //     words.replication,
        //     words.preceding,
        //     words.last,
        //   ],
        // },
        {
          id: 3,
          label: dictionary.medicine.newRecipe,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/universalRequest/1/6/94');
          },
          searchWords: [
            words.medication,
            words.medications,
            words.permanent,
            words.innovation,
            words.new,
            words.news,
            words.prescription,
            words.prescriptions,
            words.expirationEnded,
            words.inquiry,
            words.request,
          ],
        },
        {
          id: 4,
          label: dictionary.medicine.report,
          onClick: async () => {
            sendEvent(
              'Activities Page',
              'ActionDownload',
              dictionary.medicine.report
            );
            const response = await getMedConfirmationData();
            if (response.data)
              downloadFile(response.data, '.pdf', 'medication history');
            else throw new Error(dictionary.noReportError);
          },
          showIcon: true,
          searchWords: [
            words.medication,
            words.medications,
            words.history,
            words.historyOf,
            words.report,
            words.reports,
            words.issue,
            words.issuing,
          ],
        },
        {
          id: 5,
          label: dictionary.medicine.history,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/medicine/MedicationHistory');
          },
          searchWords: [
            words.medication,
            words.medications,
            words.history,
            words.historyOf,
            words.report,
            words.reports,
            words.issue,
            words.issuing,
          ],
        },
        {
          id: 6,
          label: dictionary.medicine.allOrders,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/medicine');
          },
          searchWords: [
            words.medication,
            words.medications,
            words.permanent,
            words.invitation,
            words.invitations,
            words.new,
            words.news,
            words.prescription,
            words.prescriptions,
            words.expirationEnded,
          ],
        },
      ],
    },
    {
      id: 7,
      title: dictionary.equipment.title,
      activities: [
        {
          id: 1,
          label: dictionary.equipment.newOrder,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/Equipment/createOrder');
          },
          searchWords: [
            words.equipment,
            words.consumable,
            words.invitation,
            words.invitations,
            words.fresh,
            words.news,
            words.replication,
            words.preceding,
            words.last,
            words.medical,
          ],
        },
        {
          id: 2,
          label: dictionary.equipment.duplicate,
          onClick: async () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            const lastOrderId = await getLastOrderId();
            if (lastOrderId) {
              navigate(`/Equipment/createOrder/${lastOrderId}`);
            } else throw new Error(dictionary.noLaseEquipmentOrder);
          },
          searchWords: [
            words.equipment,
            words.consumable,
            words.invitation,
            words.invitations,
            words.fresh,
            words.news,
            words.replication,
            words.preceding,
            words.last,
            words.medical,
          ],
        },
        {
          id: 3,
          label: dictionary.equipment.requestNew,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/universalRequest/1/2/39');
          },
          searchWords: [
            words.equipment,
            words.consumable,
            words.medical,
            words.fresh,
            words.inquiry,
            words.request,
            words.innovation,
          ],
        },
        {
          id: 4,
          label: dictionary.equipment.allOrders,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/Equipment');
          },
          searchWords: [
            words.equipment,
            words.consumable,
            words.invitation,
            words.invitations,
            words.fresh,
            words.news,
            words.replication,
            words.preceding,
            words.last,
            words.medical,
          ],
        },
      ],
    },
    {
      id: 8,
      title: dictionary.recognitionStatus.title,
      activities: [
        {
          id: 1,
          label: dictionary.recognitionStatus.watchStatus,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/RecognitionStatus');
          },
          searchWords: [
            words.status,
            words.statuses,
            words.treatment,
            words.situation,
            words.request,
            words.requests,
            words.inquiry,
            words.inquiries,
            words.recognition,
            words.process,
            words.onDisability,
          ],
        },
        {
          id: 2,
          label: dictionary.recognitionStatus.statusApplication,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/universalRequest/2/4');
          },
          searchWords: [
            words.status,
            words.statuses,
            words.treatment,
            words.situation,
            words.request,
            words.requests,
            words.inquiry,
            words.inquiries,
            words.recognition,
            words.process,
            words.onDisability,
          ],
        },
        {
          id: 3,
          label: dictionary.recognitionStatus.committeeApplication,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.treatments.eshetTours
            );
            navigate('/universalRequest/2/1');
          },
          searchWords: [
            words.status,
            words.statuses,
            words.treatment,
            words.situation,
            words.request,
            words.requests,
            words.inquiry,
            words.inquiries,
            words.recognition,
            words.process,
            words.onDisability,
          ],
        },
      ],
    },
    {
      id: 9,
      title: dictionary.approvals.title,
      activities: [
        ...disabilityConfirmations.map((item) => ({
          id: item.attachmentId,
          label: item.title,
          onClick: async () => {
            sendEvent('Activities Page', 'ActionDownload', item.title);
            const fileSource = await getDisabilityConfirmationPdf(
              item.attachmentId
            );
            if (fileSource)
              downloadFile(fileSource, 'pdf', item.disabilityName);
          },
          showIcon: true,
          searchWords: confirmationsSearchWords[item.attachmentId],
        })),
        {
          id: 8,
          label: dictionary.approvals.toAllApprovals,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.approvals.toAllApprovals
            );
            navigate('/disability-confirmations');
          },
          searchWords: [
            words.document,
            words.documents,
            words.file,
            words.files,
            words.toDownload,
            words.downloadOf,
            words.download,
            words.forDownload,
            words.approval,
            words.approvals,
            words.license,
            words.licenses,
          ],
        },
      ],
    },
    {
      id: 10,
      title: dictionary.yourDetails.title,
      activities: [
        {
          id: 1,
          label: dictionary.yourDetails.toYourDetails,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.yourDetails.toYourDetails
            );
            navigate('/personal-details');
          },
          searchWords: [
            words.details,
            words.my,
            words.personals,
            words.display,
            words.your,
            words.address,
            words.bankAccount,
            words.family,
            words.familyLike,
            words.kids,
            words.disabilityClauses,
            words.DisabilityPercentages,
          ],
        },
        {
          id: 2,
          label: dictionary.yourDetails.updateContact,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.yourDetails.updateContact
            );
            navigate('/requests/update-personal-details/8/18');
          },
          searchWords: [
            words.update,
            words.toUpdate,
            words.details,
            words.my,
            words.personals,
            words.your,
            words.updateAddress,
            words.updateFamilyStatus,
            words.updatePhone,
            words.updateEmail,
            words.updatePoBox,
            words.addBoy,
            words.addGirl,
            words.addHusband,
            words.addWife,
          ],
        },
        {
          id: 3,
          label: dictionary.yourDetails.updateBank,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.yourDetails.updateBank
            );
            navigate('/requests/update-bank-details/8/19');
          },
          searchWords: [
            words.update,
            words.updates,
            words.private,
            words.details,
            words.account,
            words.accounts,
            words.bank,
          ],
        },
        {
          id: 4,
          label: dictionary.yourDetails.updateAddress,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.yourDetails.updateAddress
            );
            navigate('/requests/update-personal-details/8/18');
          },
          searchWords: [
            words.update,
            words.toUpdate,
            words.details,
            words.my,
            words.personals,
            words.your,
            words.updateAddress,
            words.updateFamilyStatus,
            words.updatePhone,
            words.updateEmail,
            words.updatePoBox,
            words.addBoy,
            words.addGirl,
            words.addHusband,
            words.addWife,
          ],
        },
        {
          id: 5,
          label: dictionary.yourDetails.updateStatus,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.yourDetails.updateStatus
            );
            navigate('/requests/update-personal-details/8/18');
          },
          searchWords: [
            words.update,
            words.toUpdate,
            words.details,
            words.my,
            words.personals,
            words.your,
            words.updateAddress,
            words.updateFamilyStatus,
            words.updatePhone,
            words.updateEmail,
            words.updatePoBox,
            words.addBoy,
            words.addGirl,
            words.addHusband,
            words.addWife,
          ],
        },
        {
          id: 6,
          label: dictionary.yourDetails.addChild,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.yourDetails.addChild
            );
            navigate('/requests/update-personal-details/8/18');
          },
          searchWords: [
            words.update,
            words.toUpdate,
            words.details,
            words.my,
            words.personals,
            words.your,
            words.updateAddress,
            words.updateFamilyStatus,
            words.updatePhone,
            words.updateEmail,
            words.updatePoBox,
            words.addBoy,
            words.addGirl,
            words.addHusband,
            words.addWife,
          ],
        },
        {
          id: 7,
          label: dictionary.yourDetails.certificate,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.yourDetails.certificate
            );
            navigate('/disabled-veteran');
          },
          searchWords: [
            words.certificate,
            words.certificateOf,
            words.disabled,
            words.disability,
            words.digitally,
            words.digital,
            words.view,
            words.toView,
            words.download,
            words.downloadOf,
          ],
        },
        // {
        //   id: 8,
        //   label: dictionary.yourDetails.profilePicture,
        //   onClick: () => {
        //     sendEvent('ActionClick', dictionary.yourDetails.profilePicture);
        //     // TODO missing edit profile picture functionality
        //   },
        //   searchWords: [
        //     words.profile,
        //     words.update,
        //     words.photo,
        //     words.photos,
        //     words.change,
        //     words.delete,
        //   ],
        // },
      ],
    },
    {
      id: 11,
      title: dictionary.documents.title,
      activities: [
        {
          id: 1,
          label: dictionary.documents.yourUpdates,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.documents.yourUpdates
            );
            navigate('/updates');
          },
          searchWords: [
            words.update,
            words.updates,
            words.fresh,
            words.novelties,
          ],
        },
        {
          id: 2,
          label: dictionary.documents.yourDocuments,
          onClick: () => {
            sendEvent(
              'Activities Page',
              'ActionClick',
              dictionary.documents.yourDocuments
            );
            navigate('/documents');
          },
          searchWords: [
            words.document,
            words.documents,
            words.file,
            words.files,
            words.toDownload,
            words.downloadOf,
            words.download,
            words.forDownload,
          ],
        },
      ],
    },
  ];

  return {
    activitiesList,
  };
};
